// src/services/ezer/EzerChatService.js

export class EzerChatService {
  constructor(baseUrl, tokenProvider) {
    this.baseUrl = baseUrl
    this.tokenProvider = tokenProvider
    this.abortController = null
  }

  /**
   * Send messages to the Ezer AI backend via SSE streaming.
   * @param {Array} messages - Conversation messages
   * @param {string} locationId - Current location ID
   * @param {string} pageContext - Current page context ID
   * @param {Object} callbacks - { onText, onToolUse, onDone, onError }
   */
  async converse(messages, locationId, pageContext, callbacks) {
    // Abort any in-flight request
    this.abort()
    this.abortController = new AbortController()

    try {
      const token = this.tokenProvider()
      const response = await fetch(`${this.baseUrl}/ezer/converse`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/event-stream',
        },
        body: JSON.stringify({
          messages,
          location_id: locationId,
          page_context: pageContext,
        }),
        signal: this.abortController.signal,
      })

      if (!response.ok) {
        const errText = await response.text().catch(() => 'Unknown error')
        throw new Error(`Server error ${response.status}: ${errText}`)
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      let done = false
      while (!done) {
        const result = await reader.read()
        done = result.done
        if (done) break
        const value = result.value

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() // Keep incomplete line in buffer

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          const data = line.slice(6).trim()
          if (!data || data === '[DONE]') continue

          try {
            const event = JSON.parse(data)
            await this._handleEvent(event, callbacks)
          } catch {
            // Skip malformed JSON lines
          }
        }
      }

      callbacks.onDone?.()
    } catch (err) {
      if (err.name === 'AbortError') return
      callbacks.onError?.(err)
      callbacks.onDone?.()
    }
  }

  async _handleEvent(event, callbacks) {
    switch (event.type) {
      case 'content_block_delta':
      case 'text':
        callbacks.onText?.(event.text || event.delta?.text || '')
        break

      case 'tool_use':
        await callbacks.onToolUse?.({
          name: event.name,
          input: event.input,
          toolUseId: event.tool_use_id || event.id,
        })
        break

      case 'metadata':
        callbacks.onMetadata?.(event.usage || null, event.stop_reason || null)
        break

      case 'error':
        callbacks.onError?.(new Error(event.message || 'Stream error'))
        break

      case 'message_stop':
      case 'done':
        callbacks.onMetadata?.(null, event.stop_reason || null)
        break
    }
  }

  abort() {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
  }

  /**
   * Log conversation messages to the database via /ezer/log.
   * @param {string} sessionId
   * @param {Array} messages - [{role, content}]
   * @param {string|number} locationId
   * @param {string} pageContext
   * @param {boolean} isError
   */
  async logConversation(sessionId, messages, locationId, pageContext, isError = false, usage = null, stopReason = null) {
    try {
      const token = this.tokenProvider()
      const payload = {
        session_id: sessionId,
        messages,
        location_id: locationId,
        page_context: pageContext,
        is_error: isError,
      }
      if (usage) payload.usage = usage
      if (stopReason) payload.stop_reason = stopReason

      await fetch(`${this.baseUrl}/ezer/log`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      })
    } catch (err) {
      console.warn('Ezer: failed to log conversation', err)
    }
  }

  /**
   * Submit user satisfaction feedback for a chat session.
   * @param {string} sessionId
   * @param {boolean} satisfied
   */
  async submitFeedback(sessionId, satisfied) {
    try {
      const token = this.tokenProvider()
      await fetch(`${this.baseUrl}/ezer/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          session_id: sessionId,
          user_satisfied: satisfied,
        }),
      })
    } catch (err) {
      console.warn('Ezer: failed to submit feedback', err)
    }
  }

  /**
   * Request admin assistance for an unresolved chat session.
   * @param {string} sessionId
   * @param {number} locationId
   * @param {string} contextSummary
   * @returns {Promise<object|null>} Response JSON or null
   */
  async requestAdmin(sessionId, locationId, contextSummary) {
    try {
      const token = this.tokenProvider()
      const resp = await fetch(`${this.baseUrl}/ezer/request-admin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          session_id: sessionId,
          location_id: locationId,
          context_summary: contextSummary,
        }),
      })
      return await resp.json()
    } catch (err) {
      console.warn('Ezer: failed to request admin', err)
      return null
    }
  }
}
